import { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import * as queries from '../graphql/queries';
import { useOrganization } from '../contexts/OrganizationContext';
import { fetchAllPages } from '../utils/graphqlPagination';
import { withOrgFilter } from '../utils/orgScope';

const useUpcomingActionItems = () => {
  const [upcomingCount, setUpcomingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const { activeOrganization } = useOrganization();

  useEffect(() => {
    if (activeOrganization?.id) {
      fetchUpcomingActionItems();
    }
  }, [activeOrganization]);

  const fetchUpcomingActionItems = async () => {
    if (!activeOrganization?.id) return;
    
    setLoading(true);
    try {
      const user = await Auth.currentAuthenticatedUser();
      const userSub = user.attributes.sub;

      // Fetch reports for the current user, and projects for the active organization.
      // Each is fully paginated so nothing is dropped at the page boundary.
      const [reports, projects] = await Promise.all([
        fetchAllPages(queries.listReports, {
          // Scope to the active org, but KEEP legacy reports that have no
          // organizationID yet (visible to their owner) so existing data still shows.
          filter: withOrgFilter(
            { user_sub: { eq: userSub }, _deleted: { ne: true } },
            activeOrganization.id,
            { ownerSub: userSub, ownerField: 'user_sub' }
          )
        }),
        fetchAllPages(queries.listProjects, {
          filter: {
            organizationID: { eq: activeOrganization.id },
            _deleted: { ne: true }
          }
        })
      ]);

      // Fetch action items linked to reports - both created by user and assigned to user
      const reportActionItemPromises = reports.flatMap((report) => [
        // Items created by the user
        fetchAllPages(queries.listActionItems, {
          filter: {
            reportID: { eq: report.id },
            user_sub: { eq: userSub },
            _deleted: { ne: true },
            note: { ne: true } // Exclude notes
          }
        }),
        // Items assigned to the user
        fetchAllPages(queries.listActionItems, {
          filter: {
            reportID: { eq: report.id },
            assignees: { contains: userSub },
            _deleted: { ne: true },
            note: { ne: true } // Exclude notes
          }
        })
      ]);

      // Fetch action items linked to projects - both created by user and assigned to user
      const projectActionItemPromises = projects.flatMap((project) => [
        // Items created by the user
        fetchAllPages(queries.listActionItems, {
          filter: {
            projectID: { eq: project.id },
            user_sub: { eq: userSub },
            _deleted: { ne: true },
            note: { ne: true } // Exclude notes
          }
        }),
        // Items assigned to the user
        fetchAllPages(queries.listActionItems, {
          filter: {
            projectID: { eq: project.id },
            assignees: { contains: userSub },
            _deleted: { ne: true },
            note: { ne: true } // Exclude notes
          }
        })
      ]);

      // Wait for both report and project action items
      const allPromises = [...reportActionItemPromises, ...projectActionItemPromises];
      const allResults = await Promise.all(allPromises);

      // Combine and deduplicate action items
      const allActionItems = allResults.flat();
      const uniqueActionItems = [...new Map(allActionItems.map(item => [item.id, item])).values()];

      // Filter for upcoming action items (due today or in the future, not completed)
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset to midnight for accurate day comparison

      const upcomingItems = uniqueActionItems.filter(item => {
        if (!item.duedate || item.status === 3) return false; // Skip items without due date or completed items
        
        const dueDate = new Date(item.duedate);
        dueDate.setHours(0, 0, 0, 0); // Reset to midnight for accurate day comparison
        
        return dueDate >= today; // Due today or in the future
      });

      setUpcomingCount(upcomingItems.length);
    } catch (error) {
      console.error('Error fetching upcoming action items:', error);
      setUpcomingCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Function to manually refresh the count
  const refreshCount = () => {
    fetchUpcomingActionItems();
  };

  return {
    upcomingCount,
    loading,
    refreshCount
  };
};

export default useUpcomingActionItems;